import { CircleAlert, Info, TriangleAlert } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Callout — bloco de aviso inline.
 *
 * Anatomia do sustenta-ai-app: fundo `bg-subtle`, borda completa e um filete de
 * 3px na esquerda que carrega a cor semântica. Não é um card (não tem raio de
 * card nem sombra) justamente porque não é um objeto separado: é uma observação
 * dentro do conteúdo.
 *
 * É aqui que mora o erro que EXIGE decisão do usuário. Toast é para confirmação
 * discreta; um erro que bloqueia o fluxo tem que ficar na tela, ao lado do
 * controle que ele bloqueia (Playbook § Feedback e progresso).
 */

type Tom = 'info' | 'alerta' | 'erro';

const TONS: Record<Tom, { filete: string; icone: typeof Info; cor: string }> = {
  info: { filete: 'border-l-primary', icone: Info, cor: 'text-primary' },
  alerta: { filete: 'border-l-warning-text', icone: TriangleAlert, cor: 'text-warning-text' },
  erro: { filete: 'border-l-danger', icone: CircleAlert, cor: 'text-danger' },
};

export function Callout({
  tom = 'info',
  kicker,
  children,
  acao,
  className,
}: {
  tom?: Tom;
  /** Rótulo curto em caixa alta acima do texto — use só quando classifica. */
  kicker?: string;
  children: React.ReactNode;
  acao?: React.ReactNode;
  className?: string;
}) {
  const { filete, icone: Icone, cor } = TONS[tom];

  return (
    <div
      role={tom === 'erro' ? 'alert' : undefined}
      className={cn(
        'flex items-start gap-2.5 rounded-input border border-l-[3px] border-border bg-bg-subtle px-3.5 py-3 text-body',
        filete,
        className,
      )}
    >
      <Icone className={cn('mt-[3px] size-4 shrink-0', cor)} aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
        {kicker ? <span className="pan-eyebrow">{kicker}</span> : null}
        <div className="text-on-surface">{children}</div>
        {acao}
      </div>
    </div>
  );
}
